import React, { useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Papa from 'papaparse'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { formatCurrency } from '@/lib/utils'
import { Search, Plus, Package, X, Check, Pencil, Trash2, Tag, Upload } from 'lucide-react'
import { toast } from 'sonner'

const CSV_MAX_BYTES = 1_024 * 1_024  // 1MB cap on the imported file.

const CATEGORIES = ['Service', 'Product', 'Labour', 'Materials', 'Subscription', 'Other']
const UNITS = ['unit', 'hour', 'day', 'month', 'sq ft', 'kg', 'lb', 'item']

function ProductModal({ product, onClose, onSaved }: { product?: any; onClose: () => void; onSaved: () => void }) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({
    name: product?.name || '',
    description: product?.description || '',
    unit_price: product?.unit_price ?? 0,
    // Empty string (not 0) so a blank cost commits NULL = "unknown", never 0.
    unit_cost: product?.unit_cost ?? '',
    unit: product?.unit || 'unit',
    category: product?.category || 'Service',
    sku: product?.sku || '',
    is_active: product?.is_active ?? true,
  })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return }
    setSaving(true)
    // unit_cost: empty → null ("unknown"), never 0. A real 0 cost is allowed.
    const costStr = String(form.unit_cost).trim()
    const unit_cost = costStr === '' ? null : (Number.isFinite(parseFloat(costStr)) ? parseFloat(costStr) : null)
    const payload = { ...form, unit_price: parseFloat(String(form.unit_price)) || 0, unit_cost, workspace_id: workspace!.id, updated_at: new Date().toISOString() }
    const { error } = product
      ? await supabase.from('products').update(payload).eq('id', product.id)
      : await supabase.from('products').insert(payload)
    setSaving(false)
    if (error) { toast.error(error.message); return }
    toast.success(product ? 'Product updated' : 'Product created')
    onSaved()
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 500, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{product ? 'Edit product' : 'New product'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Website Design" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="What's included..." rows={2}
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Price</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--ink-tertiary)' }}>$</span>
                <input type="number" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} placeholder="0.00"
                  style={{ ...inp, paddingLeft: 24 }}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              </div>
            </div>
            <div>
              <label title="Internal cost — never shown to customers or vendors" style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-text)', fontStyle: 'italic', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cost</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--ink-tertiary)' }}>$</span>
                <input type="number" value={form.unit_cost} onChange={e => set('unit_cost', e.target.value)} placeholder="—"
                  title="Internal cost — never shown to customers or vendors"
                  style={{ ...inp, paddingLeft: 24, borderStyle: 'dashed' }}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Unit</label>
              <select value={form.unit} onChange={e => set('unit', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>SKU</label>
              <input value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="PROD-001" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => set('is_active', !form.is_active)}
              style={{ width: 36, height: 20, borderRadius: 10, background: form.is_active ? 'var(--accent)' : 'var(--border-default)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 150ms ease', flexShrink: 0 }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--accent-ink)', position: 'absolute', top: 3, left: form.is_active ? 19 : 3, transition: 'left 150ms ease' }} />
            </button>
            <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>Active (shows up in picker)</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, padding: '10px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 0 16px var(--accent-glow)' }}>
            {saving ? 'Saving...' : product ? 'Save changes' : 'Create product'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── CSV import ──────────────────────────────────────────────────────────────
// Insert-only, all-or-nothing commit. Headers are case-insensitive and order-
// independent. Unknown columns are dropped silently. Validation rejects rows
// inline so the user sees what failed before committing anything.

type ParsedRow = {
  name: string
  description: string | null
  unit_price: number
  unit: string
  category: string | null
  sku: string | null
  is_active: boolean
}

type PreviewRow =
  | { kind: 'ok'; row: ParsedRow; raw: Record<string, string>; index: number }
  | { kind: 'reject'; reason: string; raw: Record<string, string>; index: number }

const truncate = (v: string | undefined, n: number): string => (v ?? '').slice(0, n)

function parseBooleanCell(v: string | undefined): boolean | null {
  if (v == null || v.trim() === '') return true  // default true on blank
  const s = v.trim().toLowerCase()
  if (['true', '1', 'yes'].includes(s)) return true
  if (['false', '0', 'no'].includes(s)) return false
  return null
}

function validateRow(raw: Record<string, string>, index: number): PreviewRow {
  const name = truncate(raw.name?.trim(), 200)
  if (!name) return { kind: 'reject', reason: 'name required', raw, index }

  const priceRaw = (raw.unit_price ?? '').trim()
  let unit_price = 0
  if (priceRaw !== '') {
    const parsed = parseFloat(priceRaw)
    if (!Number.isFinite(parsed)) return { kind: 'reject', reason: 'unit_price not numeric', raw, index }
    unit_price = parsed
  }

  const is_active = parseBooleanCell(raw.is_active)
  if (is_active === null) return { kind: 'reject', reason: 'is_active not parseable', raw, index }

  return {
    kind: 'ok',
    raw,
    index,
    row: {
      name,
      description: raw.description?.trim() ? truncate(raw.description.trim(), 1000) : null,
      unit_price,
      unit: truncate(raw.unit?.trim() || 'unit', 50),
      category: raw.category?.trim() ? truncate(raw.category.trim(), 100) : null,
      sku: raw.sku?.trim() ? truncate(raw.sku.trim(), 100) : null,
      is_active,
    },
  }
}

function CsvImportModal({
  workspaceId, rows, onClose, onImported,
}: {
  workspaceId: string
  rows: PreviewRow[]
  onClose: () => void
  onImported: () => void
}) {
  const queryClient = useQueryClient()
  const okRows = rows.filter((r): r is Extract<PreviewRow, { kind: 'ok' }> => r.kind === 'ok')
  const rejectedCount = rows.length - okRows.length
  const [committing, setCommitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleCommit = async () => {
    if (okRows.length === 0) return
    setCommitting(true)
    setErrorMessage(null)
    const payload = okRows.map(r => ({ ...r.row, workspace_id: workspaceId }))
    const { error } = await supabase.from('products').insert(payload)
    setCommitting(false)
    if (error) { setErrorMessage(error.message); return }
    queryClient.invalidateQueries({ queryKey: ['products'] })
    toast.success(`Imported ${okRows.length} product${okRows.length === 1 ? '' : 's'}`)
    onImported()
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 24, width: '100%', maxWidth: 760, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Preview import</h2>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>{okRows.length} ready, {rejectedCount} rejected.</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'var(--hover-bg)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 120px 90px 90px 130px', gap: 0, padding: '8px 14px', borderBottom: '1px solid var(--border-subtle)', position: 'sticky', top: 0, background: 'var(--surface)' }}>
            {['#', 'Name', 'Category', 'Price', 'Active', 'Status'].map((h, i) => (
              <div key={i} style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</div>
            ))}
          </div>
          {rows.map(r => {
            const isOk = r.kind === 'ok'
            const display = isOk ? r.row : null
            return (
              <div key={r.index} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 120px 90px 90px 130px', gap: 0, padding: '8px 14px', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center', fontSize: 12, color: 'var(--ink-secondary)' }}>
                <span style={{ color: 'var(--ink-tertiary)' }}>{r.index + 1}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display?.name ?? r.raw.name ?? '—'}</span>
                <span style={{ color: 'var(--ink-secondary)' }}>{display?.category ?? r.raw.category ?? '—'}</span>
                <span>{display ? formatCurrency(display.unit_price) : (r.raw.unit_price ?? '—')}</span>
                <span style={{ color: 'var(--ink-secondary)' }}>{display ? (display.is_active ? 'Yes' : 'No') : (r.raw.is_active ?? '—')}</span>
                {isOk ? (
                  <span style={{ color: 'var(--positive)', fontSize: 11, fontWeight: 600 }}>✓ Ready</span>
                ) : (
                  <span style={{ color: 'var(--danger)', fontSize: 11, fontWeight: 600 }}>✗ {r.reason}</span>
                )}
              </div>
            )
          })}
          {rows.length === 0 && (
            <p style={{ padding: '20px', fontSize: 12, color: 'var(--ink-tertiary)', textAlign: 'center', margin: 0 }}>No rows parsed from this file.</p>
          )}
        </div>

        {errorMessage && (
          <p style={{ marginTop: 12, padding: '10px 12px', background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', borderRadius: 8, color: 'var(--danger)', fontSize: 12 }}>
            Import failed: {errorMessage}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleCommit} disabled={committing || okRows.length === 0}
            style={{ flex: 1, padding: '10px', background: okRows.length === 0 ? 'var(--field-bg)' : 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: okRows.length === 0 ? 'not-allowed' : 'pointer', opacity: okRows.length === 0 ? 0.5 : 1 }}>
            {committing ? 'Importing…' : `Import ${okRows.length} row${okRows.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default function Products() {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<any>(null)
  const [previewRows, setPreviewRows] = useState<PreviewRow[] | null>(null)
  const csvInputRef = useRef<HTMLInputElement>(null)

  const handleCsvPick = (file: File | null) => {
    if (!file) return
    if (file.size > CSV_MAX_BYTES) {
      toast.error(`File is ${(file.size / 1024 / 1024).toFixed(2)} MB — max is 1 MB.`)
      return
    }
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim().toLowerCase(),
      complete: (result) => {
        const parsed = (result.data || []).map((raw, i) => validateRow(raw, i))
        setPreviewRows(parsed)
        if (csvInputRef.current) csvInputRef.current.value = ''
      },
      error: (err) => {
        toast.error(`CSV parse failed: ${err.message}`)
        if (csvInputRef.current) csvInputRef.current.value = ''
      },
    })
  }

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('products').select('*').eq('workspace_id', workspace.id).order('name')
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('products').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }); toast.success('Deleted') },
    onError: () => toast.error('Failed to delete'),
  })

  const filtered = products.filter((p: any) => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase())
    const matchCat = categoryFilter === 'all' || p.category === categoryFilter
    return matchSearch && matchCat
  })

  const categories = ['all', ...Array.from(new Set(products.map((p: any) => p.category).filter(Boolean)))] as string[]

  const totalActive = products.filter((p: any) => p.is_active).length
  const avgPrice = products.length ? products.reduce((s: number, p: any) => s + (p.unit_price || 0), 0) / products.length : 0

  return (
    <div style={{ height: '100%', overflow: 'auto', background: 'var(--canvas)' }}>
      <div style={{ padding: '28px 32px', maxWidth: 1100 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: '0 0 6px' }}>Products & Services</h1>
            <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>Reusable catalog for quotes and invoices</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={e => handleCsvPick(e.target.files?.[0] ?? null)}
            />
            <button onClick={() => csvInputRef.current?.click()}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 11, color: 'var(--ink-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <Upload style={{ width: 14, height: 14 }} /> Import CSV
            </button>
            <button onClick={() => { setEditing(null); setShowModal(true) }}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 11, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 0 20px var(--accent-glow)' }}>
              <Plus style={{ width: 15, height: 15 }} /> New product
            </button>
          </div>
        </div>

        {/* Stats — auto-fit so cards reflow on narrow viewports instead
            of compressing 3-up to unreadable widths on mobile. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Total products', value: products.length, color: 'var(--accent)' },
            { label: 'Active', value: totalActive, color: 'var(--positive)' },
            { label: 'Avg price', value: formatCurrency(avgPrice), color: 'var(--warning)' },
          ].map(stat => (
            <div key={stat.label} style={{ padding: '16px 20px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 14 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>{stat.label}</p>
              <p style={{ fontSize: 22, fontWeight: 900, color: stat.color, margin: 0, letterSpacing: '-0.02em' }}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Search + filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products, SKUs..."
              style={{ width: '100%', padding: '9px 12px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--accent-border)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {categories.map(cat => (
              <button key={cat} onClick={() => setCategoryFilter(cat)}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: categoryFilter === cat ? 'var(--accent)' : 'var(--hover-bg)', color: categoryFilter === cat ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: categoryFilter === cat ? 'none' : '1px solid var(--border-default)', transition: 'all 150ms ease' }}>
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-tertiary)' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px' }}>
            <Package style={{ width: 40, height: 40, color: 'var(--border-default)', margin: '0 auto 14px', display: 'block' }} />
            <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-tertiary)', margin: '0 0 6px' }}>
              {search ? 'No products match your search' : 'No products yet'}
            </p>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 20px' }}>
              {search ? 'Try a different search' : 'Add your services and products to use them in quotes and invoices'}
            </p>
            {!search && (
              <button onClick={() => { setEditing(null); setShowModal(true) }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                <Plus style={{ width: 14, height: 14 }} /> Add first product
              </button>
            )}
          </div>
        ) : (
          <div style={{ background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 16, overflow: 'hidden' }}>
            {/* Table header — desktop only. Cards on mobile are self-labeling. */}
            {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 80px 100px 100px 80px', gap: 0, padding: '10px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
              {['Product', 'Category', 'SKU', 'Price', 'Unit', ''].map((h, i) => (
                <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</div>
              ))}
            </div>
            )}

            {filtered.map((product: any, i: number) => {
              const isLast = i === filtered.length - 1

              // Mobile card. Products has no row-level navigate — the
              // only interactivity is the Edit/Delete buttons, so those
              // are preserved as touch-friendly affordances at the
              // bottom-right of the card. Price is the headline (right-
              // aligned on the header row, large+bold). Inactive badge
              // kept inline next to the name.
              // Mobile branch uses CSS-var colors only — the desktop branch
              // keeps its legacy hex/rgba palette unchanged.
              if (isMobile) {
                return (
                  <motion.div key={product.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)', transition: 'background 150ms ease' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Package style={{ width: 16, height: 16, color: 'var(--accent-text)' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</p>
                          {!product.is_active && (
                            <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--ink-tertiary)', background: 'var(--hover-bg)', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', flexShrink: 0 }}>Inactive</span>
                          )}
                        </div>
                        {product.description && (
                          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.description}</p>
                        )}
                      </div>
                      <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, flexShrink: 0, letterSpacing: '-0.01em' }}>{formatCurrency(product.unit_price || 0)}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                        {product.category && (
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', padding: '3px 9px', borderRadius: 100 }}>
                            {product.category}
                          </span>
                        )}
                        {product.sku && (
                          <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', fontFamily: 'monospace' }}>{product.sku}</span>
                        )}
                        <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>per {product.unit || 'unit'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button onClick={() => { setEditing(product); setShowModal(true) }}
                          aria-label={`Edit ${product.name}`}
                          style={{ width: 32, height: 32, borderRadius: 7, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Pencil style={{ width: 13, height: 13 }} />
                        </button>
                        <button onClick={() => { if (confirm(`Delete "${product.name}"?`)) deleteMutation.mutate(product.id) }}
                          aria-label={`Delete ${product.name}`}
                          style={{ width: 32, height: 32, borderRadius: 7, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Trash2 style={{ width: 13, height: 13 }} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )
              }

              // Desktop grid row — unchanged.
              return (
              <motion.div key={product.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                style={{ display: 'grid', gridTemplateColumns: '1fr 120px 80px 100px 100px 80px', gap: 0, padding: '14px 20px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none', alignItems: 'center', transition: 'background 150ms ease' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                {/* Name + description */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, overflow: 'hidden' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Package style={{ width: 16, height: 16, color: 'var(--accent-text)' }} />
                  </div>
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</p>
                      {!product.is_active && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--ink-tertiary)', background: 'var(--field-bg)', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', flexShrink: 0 }}>Inactive</span>
                      )}
                    </div>
                    {product.description && (
                      <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.description}</p>
                    )}
                  </div>
                </div>

                {/* Category */}
                <div>
                  {product.category && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', padding: '3px 9px', borderRadius: 100 }}>
                      {product.category}
                    </span>
                  )}
                </div>

                {/* SKU */}
                <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, fontFamily: 'monospace' }}>{product.sku || '—'}</p>

                {/* Price */}
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{formatCurrency(product.unit_price || 0)}</p>

                {/* Unit */}
                <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>per {product.unit || 'unit'}</p>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button onClick={() => { setEditing(product); setShowModal(true) }}
                    style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--field-bg)', border: '1px solid var(--border-default)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--ink-primary)'; e.currentTarget.style.borderColor = 'var(--border-strong)' }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)'; e.currentTarget.style.borderColor = 'var(--border-default)' }}>
                    <Pencil style={{ width: 11, height: 11 }} />
                  </button>
                  <button onClick={() => { if (confirm(`Delete "${product.name}"?`)) deleteMutation.mutate(product.id) }}
                    style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--field-bg)', border: '1px solid var(--border-default)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.borderColor = 'var(--danger-border)' }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)'; e.currentTarget.style.borderColor = 'var(--border-default)' }}>
                    <Trash2 style={{ width: 11, height: 11 }} />
                  </button>
                </div>
              </motion.div>
              )
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showModal && (
          <ProductModal
            product={editing}
            onClose={() => { setShowModal(false); setEditing(null) }}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['products'] })}
          />
        )}
        {previewRows && workspace?.id && (
          <CsvImportModal
            workspaceId={workspace.id}
            rows={previewRows}
            onClose={() => setPreviewRows(null)}
            onImported={() => setPreviewRows(null)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}